<template>
  <div class="page-shell">
    <nav class="flex items-center gap-1" aria-label="Хлебные крошки">
      <NuxtLink
        to="/search"
        class="font-helvetica-400 text-[16px] font-normal text-extra-text-color transition-colors hover:text-orange"
      >
        Каталог
      </NuxtLink>
      <span
        class="px-1 font-helvetica-400 text-[16px] font-normal text-extra-text-color"
      >
        /
      </span>
      <span class="font-helvetica-400 text-[16px] font-normal text-gray-dark">
        {{ typeName }}
      </span>
    </nav>

    <p
      class="mobile-page-title font-helvetica-700 font-bold uppercase text-gray-dark"
    >
      {{ typeName }}
    </p>

    <div
      class="grid min-w-0 grid-cols-1 gap-6 min-[1280px]:grid-cols-[minmax(280px,340px)_minmax(0,1fr)] min-[1500px]:grid-cols-[minmax(320px,373px)_minmax(0,1fr)]"
    >
      <SelectorGroup
        class="hidden min-w-0 min-[1280px]:flex"
        :code="typeCode"
        :is-tyres="typeKey === 'tires'"
        :initial-filters="initialFilters"
        :initial-diameter="activeDiameter"
        :initial-sizes="activeSizes"
        @apply="handleApplyFilters"
      />

      <div ref="goodsSectionRef" class="flex min-w-0 w-full flex-col gap-5">
        <button
          type="button"
          class="grid h-14 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-[20px] border border-gray-border bg-white px-4 text-left text-gray-dark shadow-panel-soft min-[1280px]:hidden"
          @click="mobileFiltersOpen = true"
        >
          <span class="font-helvetica-700 text-[18px] leading-none"
            >Фильтры</span
          >
          <span
            class="inline-flex h-8 w-8 flex-col items-center justify-center gap-[3px] text-orange"
          >
            <span class="h-[2px] w-[14px] rounded-full bg-current"></span>
            <span class="h-[2px] w-[14px] rounded-full bg-current"></span>
            <span class="h-[2px] w-[14px] rounded-full bg-current"></span>
          </span>
        </button>

        <UpperFilter :count="totalCount" v-model:sort="sortValue" />

        <div v-if="isLoading" class="flex items-center justify-center py-10">
          <div class="flex flex-col items-center gap-3 text-gray-dark">
            <div
              class="h-10 w-10 animate-spin rounded-full border-4 border-gray-light border-t-orange"
            ></div>
            <p class="font-helvetica-400 text-[16px]">Загрузка товаров...</p>
          </div>
        </div>

        <div v-else-if="goods.length" class="flex flex-col gap-2.5">
          <GoodsCard
            v-for="item in goods"
            :key="item.id"
            :id="item.id"
            :title="item.title"
            :brand="item.brand"
            :article="item.article"
            :price="item.price"
            :discount-price="item.discountPrice"
            :discount-percent="item.discountPercent"
            :price-badge-label="item.priceBadgeLabel"
            :image="item.image"
            :media="item.media"
            :warehouses="item.warehouses"
            :is-favorite="item.isFavorite"
            :warehouses-list="item.warehousesList"
          />
        </div>

        <NoGoods v-else />

        <Pagination
          v-if="totalPages > 1"
          :current-page="currentPage"
          :total-pages="totalPages"
          @change-page="onChangePage"
        />
      </div>
    </div>

    <div
      v-if="mobileFiltersOpen"
      class="fixed inset-0 z-[1200] bg-black/30 min-[1280px]:hidden"
      @click.self="mobileFiltersOpen = false"
    >
      <section
        class="absolute inset-x-0 bottom-0 max-h-[88dvh] overflow-y-auto rounded-t-[30px] bg-white px-4 pb-0 pt-4 shadow-[0_-12px_32px_rgb(0_0_0_/_0.12)]"
      >
        <div
          class="mx-auto flex w-full max-w-[var(--layout-mobile-max-width)] flex-col gap-4"
        >
          <div class="relative flex items-center justify-center">
            <p
              class="text-[26px] leading-none font-helvetica-700 text-gray-dark"
            >
              Фильтры
            </p>

            <button
              type="button"
              class="absolute right-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-border bg-white-gray text-gray-dark"
              aria-label="Закрыть фильтры"
              @click="mobileFiltersOpen = false"
            >
              <span
                class="absolute h-[1.5px] w-4 rotate-45 rounded-full bg-current"
              ></span>
              <span
                class="absolute h-[1.5px] w-4 -rotate-45 rounded-full bg-current"
              ></span>
            </button>
          </div>

          <SelectorGroup
            class="min-w-0"
            :code="typeCode"
            :is-tyres="typeKey === 'tires'"
            :initial-filters="initialFilters"
            :initial-diameter="activeDiameter"
            :initial-sizes="activeSizes"
            :mobile="true"
            @apply="handleMobileApplyFilters"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick } from "vue";
import SelectorGroup from "~/components/goods/SelectorGroup.vue";
import GoodsCard from "~/components/goods/GoodsCard.vue";
import UpperFilter from "~/components/goods/UpperFilter.vue";
import Pagination from "~/components/goods/Pagination.vue";
import NoGoods from "~/components/goods/NoGoods.vue";
import { useCatalogViewCache } from "~/composables/useCatalogViewCache";
import type { GoodsObject } from "~/types/goodsObject";
import { normalizeMediaUrl } from "~/utils/normalizeMediaUrl";
import {
  collectProductMediaLinks,
  getPrimaryImageFromMedia,
} from "~/utils/productMedia";
import { getPriceBadgeLabel } from "~/utils/pricing";
import { useRouter } from "#app";
import { useNuxtApp } from "#imports";

const codeMap = new Map([
  ["tires", 1],
  ["wheel", 2],
  ["axles", 3],
  ["hydraulics", 4],
  ["discs", 5],
  ["staf", 6],
  ["cam", 7],
]);

const nameMap = new Map<string, string>([
  ["tires", "Шины"],
  ["wheel", "Колесо в сборе"],
  ["axles", "Оси и ступицы"],
  ["hydraulics", "Гидравлика"],
  ["discs", "Диски колесные"],
  ["staf", "Запчасти"],
  ["cam", "Камеры, ленты, кольца"],
]);

definePageMeta({
  keepalive: true,
  validate(route) {
    return Array.from(codeMap.keys()).includes(String(route.params.type));
  },
});

const route = useRoute();
const router = useRouter();
const nuxtApp = useNuxtApp();
const $useOneCApi = nuxtApp.$useOneCApi as (
  action: string,
  payload?: Record<string, any>,
) => Promise<any>;
const { pageResults } = useCatalogViewCache();

const typeKey = computed(() => String(route.params.type));
const typeCode = computed(() => codeMap.get(typeKey.value) ?? 0);
const typeName = computed(() => nameMap.get(typeKey.value) ?? "");
const DEFAULT_SORT = "name-asc";
const limit = 15;

const cleanQuery = (query: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return value !== undefined && value !== null && value !== "";
    }),
  );

const sortModeMap: Record<string, number> = {
  "name-asc": 0,
  "cheap-first": 1,
  "name-desc": 3,
  "expensive-first": 4,
};

const parseSortQuery = (value: any) =>
  typeof value === "string" && value in sortModeMap ? value : DEFAULT_SORT;

const parsePageQuery = (value: any) => {
  const normalized = Array.isArray(value) ? value[0] : value;
  const parsed = Number(normalized);

  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }

  return Math.floor(parsed);
};

const parseListQuery = (value: any) => {
  const source = Array.isArray(value) ? value.join(";") : value;

  if (typeof source !== "string") {
    return [] as string[];
  }

  return source
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);
};

const initialFilters = computed(() =>
  typeof route.query.filters === "string" ? route.query.filters : "",
);
const activeDiameter = computed(() =>
  typeof route.query.diameter === "string" ? route.query.diameter : "",
);
const activeSizes = computed(() => parseListQuery(route.query.sizes));

usePageTitle(
  computed(() => (typeName.value ? `Каталог: ${typeName.value}` : "Каталог")),
);

const goods = ref<GoodsObject[]>([]);
const totalCount = ref<number>(0);
const isLoading = ref(false);
const mobileFiltersOpen = ref(false);
const goodsSectionRef = ref<HTMLElement | null>(null);

const sortValue = computed({
  get: () => parseSortQuery(route.query.sort),
  set: (value: string) => {
    const nextSort = parseSortQuery(value);

    router.replace({
      query: cleanQuery({
        ...route.query,
        sort: nextSort === DEFAULT_SORT ? undefined : nextSort,
        page: undefined,
      }),
    });
  },
});

const currentPage = computed(() => parsePageQuery(route.query.page));
const sortMode = computed(() => sortModeMap[sortValue.value] ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalCount.value / limit)),
);
const cacheKey = computed(() => `type:${route.fullPath}`);
const useTypeSizeSearch = computed(
  () =>
    typeKey.value === "tires" &&
    (!!activeDiameter.value || activeSizes.value.length > 0),
);

const normalizePrice = (price: any) => {
  if (typeof price === "number") {
    return price;
  }

  if (typeof price === "string") {
    const cleaned = price.replace(/[\s\u00A0]/g, "").replace(",", ".");
    const parsed = Number(cleaned);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    return price;
  }

  return 0;
};

const parsePriceNumber = (value: any) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/[\s\u00A0]/g, "").replace(",", ".");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const parsePositiveNumber = (value: any) => {
  const parsed = parsePriceNumber(value);
  return parsed > 0 ? parsed : 0;
};

const getWarehousesCount = (stocks: any) => {
  if (!Array.isArray(stocks)) {
    return 0;
  }

  return stocks.reduce((acc: number, stock: any) => {
    const availableRaw = stock?.["Доступно"] ?? stock?.["ВНаличии"];
    const available = Number(availableRaw);
    return acc + (Number.isFinite(available) && available > 0 ? 1 : 0);
  }, 0);
};

const mapProductToGoods = (product: any): GoodsObject => {
  const imagePath = product?.["ФайлКартинки"] || product?.["ПутьКФото"];
  const media = collectProductMediaLinks(product, [imagePath]);
  const normalizedImage =
    normalizeMediaUrl(imagePath) ||
    getPrimaryImageFromMedia(media) ||
    "/logo.webp";

  const basePriceRaw = product?.["Цена"];
  const discountPriceRaw = product?.["ЦенаСоСкидкой"];
  const discountPercentRaw =
    product?.["СкидкаПроцент"] ??
    product?.["ПроцентСкидки"] ??
    product?.["СкидкаПроцентом"];
  const discountPercent = parsePositiveNumber(discountPercentRaw);
  const basePriceNum = parsePriceNumber(basePriceRaw);
  const discountPriceNum = parsePriceNumber(discountPriceRaw);
  const hasDiscount =
    discountPriceNum > 0 && basePriceNum > 0 && discountPriceNum < basePriceNum;
  const priceSource = hasDiscount
    ? discountPriceRaw
    : (discountPriceRaw ?? basePriceRaw);
  const favoriteRaw = product?.["Избранное"];
  const favoriteFlag =
    favoriteRaw === "1" || favoriteRaw === 1 || favoriteRaw === true;
  const stocks = Array.isArray(product?.["ОстаткиПоСкладам"])
    ? product["ОстаткиПоСкладам"]
    : [];

  return {
    id: product?.["B2BКод"] ?? "",
    title: product?.["Наименование"] ?? "",
    brand: product?.["Производитель"] ?? "",
    article:
      product?.["АртикулПроизводителя"] ?? product?.["КодНоменклатуры"] ?? "",
    price: normalizePrice(priceSource),
    discountPrice: hasDiscount ? normalizePrice(basePriceRaw) : undefined,
    discountPercent:
      hasDiscount && discountPercent > 0 ? discountPercent : undefined,
    priceBadgeLabel: getPriceBadgeLabel(product?.["ЦеноваяГруппа"]),
    image: normalizedImage,
    media,
    warehouses: getWarehousesCount(stocks),
    warehousesList: stocks,
    isFavorite: favoriteFlag,
  };
};

const replacePageQuery = (page: number) =>
  router.replace({
    query: cleanQuery({
      ...route.query,
      page: page === 1 ? undefined : String(page),
    }),
  });

const ensureValidCurrentPage = async () => {
  const maxPage = totalPages.value;
  if (currentPage.value <= maxPage) return false;
  await replacePageQuery(maxPage);
  return true;
};

const getCatalogScrollOffset = () => {
  if (!process.client) return 0;

  const isDesktop = window.matchMedia("(min-width: 1176px)").matches;
  const variableName = isDesktop
    ? "--layout-header-height"
    : "--layout-mobile-header-height";
  const rawValue = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
  const parsed = Number.parseFloat(rawValue);

  return Number.isFinite(parsed) ? parsed + 12 : isDesktop ? 107 : 84;
};

const scrollToGoodsStart = async () => {
  if (!process.client) return;
  await nextTick();

  const element = goodsSectionRef.value;
  if (!element) return;

  const top =
    element.getBoundingClientRect().top +
    window.scrollY -
    getCatalogScrollOffset();

  window.scrollTo({
    top: Math.max(0, top),
    behavior: "smooth",
  });
};

const fetchGoods = async (options?: { force?: boolean; silent?: boolean }) => {
  if (typeof $useOneCApi !== "function" || !typeCode.value) {
    return;
  }

  const cached = pageResults.value[cacheKey.value];
  if (cached && !options?.force) {
    goods.value = cached.goods;
    totalCount.value = cached.totalCount;
    isLoading.value = false;
    await ensureValidCurrentPage();
    return;
  }

  if (!options?.silent) {
    isLoading.value = true;
  }

  try {
    let parameters: Record<string, any>;

    if (useTypeSizeSearch.value) {
      parameters = {
        search_type: "tyres",
        sort: sortMode.value,
        currentPage: currentPage.value,
        limit,
      };

      if (activeSizes.value.length) {
        parameters.tyres_typorazmer = activeSizes.value.join(":");
      }

      if (activeDiameter.value) {
        parameters.tyres_diameter = activeDiameter.value;
      }
    } else {
      parameters = {
        group_id: typeCode.value,
        sort: sortMode.value,
        currentPage: currentPage.value,
        limit,
        filters: initialFilters.value,
      };
    }

    const response = await $useOneCApi("getProductSearch", {
      parametersJSON: JSON.stringify(parameters),
    });

    const products = Array.isArray(response?.["Товары"])
      ? response["Товары"]
      : [];
    const nextGoods = products.map(mapProductToGoods);

    const totalRaw =
      response?.["ВсегоКоличествоТоваров"] ??
      response?.["Итоги"]?.["КоличествоТоваров"];
    const totalParsed =
      typeof totalRaw === "string"
        ? Number(totalRaw.replace(/[\s\u00A0]/g, "").replace(",", "."))
        : Number(totalRaw);

    totalCount.value = Number.isFinite(totalParsed)
      ? totalParsed
      : nextGoods.length;

    if (await ensureValidCurrentPage()) {
      return;
    }

    goods.value = nextGoods;

    pageResults.value[cacheKey.value] = {
      goods: goods.value,
      totalCount: totalCount.value,
    };
  } catch (error) {
    console.error("Ошибка загрузки каталога", error);
    if (!options?.silent) {
      goods.value = [];
      totalCount.value = 0;
    }
  } finally {
    if (!options?.silent) {
      isLoading.value = false;
    }
  }
};

watch(
  () => [
    typeCode.value,
    initialFilters.value,
    activeDiameter.value,
    activeSizes.value.join(";"),
    sortMode.value,
    currentPage.value,
  ],
  () => {
    fetchGoods();
  },
  { immediate: true },
);

const handleApplyFilters = (payload: {
  filters: string;
  sizes: string[];
  diameter: string;
}) => {
  const nextQuery: Record<string, any> = {
    ...route.query,
    page: undefined,
  };

  if (typeKey.value === "tires" && (payload.sizes.length || payload.diameter)) {
    nextQuery.sizes = payload.sizes.length
      ? payload.sizes.join(";")
      : undefined;
    nextQuery.diameter = payload.diameter || undefined;
    nextQuery.filters = undefined;
  } else {
    nextQuery.filters = payload.filters || undefined;
    nextQuery.sizes = undefined;
    nextQuery.diameter = undefined;
  }

  router.replace({
    query: cleanQuery(nextQuery),
  });
};

const handleMobileApplyFilters = (payload: {
  filters: string;
  sizes: string[];
  diameter: string;
}) => {
  mobileFiltersOpen.value = false;
  handleApplyFilters(payload);
};

const onChangePage = (page: number) => {
  const nextPage = Math.max(1, page);

  if (nextPage === currentPage.value) {
    return;
  }

  void replacePageQuery(nextPage).then(() => {
    void scrollToGoodsStart();
  });
};

usePeriodicRefresh(
  async () => {
    await fetchGoods({ force: true, silent: true });
  },
  {
    enabled: () => Boolean(typeCode.value),
  },
);
</script>

<style></style>
